/**
 * `R4-H-001` · La forma REAL que manda `apps/admin-web/src/situaciones-criticas.ts`
 * y su paso a texto, en UN solo lugar (función pura).
 *
 * - `categoria` llega como `{ nombre, tipo }` o `null` (contención genérica).
 * - `estado_mensaje` llega como el `EstadoMensaje` crudo de `contracts/conversation`.
 *
 * PRD_R4.md:881 (Categoría) y :900 (Estado del panel). Esta pantalla no puede
 * importar de `conversaciones/**`, por eso la etiqueta se escribe acá.
 */

export interface CategoriaEnLaRed {
  readonly nombre: string
  readonly tipo: string
}

export type EstadoMensajeEnLaRed =
  | { readonly caso: "no_aplica" }
  | { readonly caso: "sin_reporte" }
  | { readonly caso: "reportado"; readonly estado: string; readonly provider_message_id?: string }

export const TEXTO_SIN_CATEGORIA = "Tema sensible sin categoría"
const SIN_VALOR = "—"

const ETIQUETA_DEL_ESTADO: Readonly<Record<string, string>> = {
  enviado: "Enviado",
  entregado: "Entregado",
  leido: "Leído",
  fallido: "Fallido",
}

function plegado(valor: string): string {
  return valor
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim()
}

/**
 * La celda Categoría (PRD:881; CA-49/CA-50). Con ficha, su nombre. Los dos `null`
 * se distinguen por `id_categoria` (`FilaDeCaso`, `contracts/conversation`):
 * `null` CON id es una categoría que ya no existe ⇒ «—»; `null` SIN id es la
 * contención genérica ⇒ «Tema sensible sin categoría».
 */
export function textoDeCategoria(
  categoria: CategoriaEnLaRed | null,
  idCategoria?: string | null,
): string {
  if (categoria !== null) return categoria.nombre
  return idCategoria === undefined || idCategoria === null || idCategoria === ""
    ? TEXTO_SIN_CATEGORIA
    : SIN_VALOR
}

/** Un dato que puede venir vacío (teléfono, lote): vacío ⇒ «—», nunca un hueco (CA-36). */
export function textoOGuion(valor: string | null | undefined): string {
  return valor === null || valor === undefined || valor.trim() === "" ? SIN_VALOR : valor
}

/**
 * Con qué se abre la conversación de un caso: el `segmento` de las rutas, sólo si
 * el caso es de una RESPUESTA. Un caso de `turn_log` (anterior a R4) no tiene
 * conversación y la ruta daría 404 (CA-47/CA-48). `segmentoParaAbrir` lo fija el
 * borde; sin él (dobles viejos) vale `id_conversacion`.
 */
export function segmentoParaAbrir(caso: {
  readonly id_conversacion: string | null
  readonly segmentoParaAbrir?: string | null
}): string | null {
  return caso.segmentoParaAbrir === undefined ? caso.id_conversacion : caso.segmentoParaAbrir
}

/** El Estado de un mensaje del panel (PRD:900); «—» cuando no aplica o no hay reporte. */
export function textoDeEstadoDeMensaje(estado: EstadoMensajeEnLaRed): string {
  if (estado.caso !== "reportado") return SIN_VALOR
  return ETIQUETA_DEL_ESTADO[plegado(estado.estado)] ?? estado.estado
}
